import { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Switch,
    Box,
    Button,
    Stack,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import {
    collection,
    onSnapshot,
    query,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNotify } from 'react-admin';
import { db } from '../providers/firebase/firebase';
import { ALERT_TYPE_LABELS } from '../pages/markets/components/AlertRow';
import {
    SEVERITY_LABEL,
    SEVERITY_ORDER,
    SEVERITY_TOOLTIP,
} from './severityLabel';
import { useNotificationSettings } from './hooks/useNotificationSettings';
import { useFcmToken } from './hooks/useFcmToken';
import { SubscribePopover } from './SubscribePopover';
import { useSubscription } from './hooks/useSubscription';
import type {
    SubscriptionDoc,
    TraderSubscriptionDoc,
} from '../types/notifications';
import { openInNewTab } from '../utils/newTab';

function GlobalDeliveryCard() {
    const { settings, save } = useNotificationSettings();
    const { permission, enabledOnThisDevice, ensureToken, revokeToken } =
        useFcmToken();

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="overline" sx={{ fontWeight: 600 }}>
                    Delivery
                </Typography>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1 }}
                >
                    <Box>
                        <Typography variant="body2">
                            Browser push (this device)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Notifications on this device, even when the tab is
                            closed
                            {permission === 'denied' &&
                                ' — denied at browser level; allow in browser settings'}{' '}
                            On iPhone, open this site in Safari (not Chrome) and
                            add it to your Home Screen to enable push.
                        </Typography>
                    </Box>
                    <Switch
                        checked={
                            !!enabledOnThisDevice && permission === 'granted'
                        }
                        disabled={
                            permission === 'denied' ||
                            enabledOnThisDevice === undefined
                        }
                        onChange={async e => {
                            if (e.target.checked) {
                                await ensureToken();
                            } else {
                                await revokeToken();
                            }
                        }}
                    />
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box>
                        <Typography variant="body2">
                            Min severity (global)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Applied on top of per-event severity filters
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {SEVERITY_ORDER.map(s => (
                            <Button
                                key={s}
                                size="small"
                                variant={
                                    settings.min_severity === s
                                        ? 'contained'
                                        : 'outlined'
                                }
                                onClick={() => save({ min_severity: s })}
                                title={SEVERITY_TOOLTIP[s]}
                                sx={{
                                    minWidth: 0,
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '0.7rem',
                                }}
                            >
                                {SEVERITY_LABEL[s]}
                            </Button>
                        ))}
                    </Box>
                </Stack>

                <Box
                    sx={{
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body2">
                        Enabled alert types (global)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Disabling here suppresses across all your events
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            mt: 1,
                        }}
                    >
                        {Object.entries(ALERT_TYPE_LABELS).map(
                            ([key, label]) => {
                                const enabled =
                                    settings.enabled_types === null ||
                                    settings.enabled_types.includes(key);
                                return (
                                    <Chip
                                        key={key}
                                        label={label}
                                        color={enabled ? 'primary' : 'default'}
                                        variant={
                                            enabled ? 'filled' : 'outlined'
                                        }
                                        size="small"
                                        onClick={() => {
                                            const currentlyOn =
                                                settings.enabled_types === null
                                                    ? Object.keys(
                                                          ALERT_TYPE_LABELS
                                                      )
                                                    : settings.enabled_types;
                                            const next = enabled
                                                ? currentlyOn.filter(
                                                      t => t !== key
                                                  )
                                                : [...currentlyOn, key];
                                            save({
                                                enabled_types:
                                                    next.length ===
                                                    Object.keys(
                                                        ALERT_TYPE_LABELS
                                                    ).length
                                                        ? null
                                                        : next,
                                            });
                                        }}
                                    />
                                );
                            }
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function formatRelative(ts: unknown): string {
    if (!ts) return '';
    const date =
        typeof (ts as any).toDate === 'function'
            ? (ts as any).toDate()
            : new Date(ts as any);
    const diffMs = Date.now() - date.getTime();
    const days = Math.floor(diffMs / 86_400_000);
    if (days < 1) return 'today';
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
}

function SubscribedEventsTable() {
    const [uid, setUid] = useState<string | null>(null);
    const [subs, setSubs] = useState<SubscriptionDoc[]>([]);
    const [editing, setEditing] = useState<{
        eventId: string;
        anchor: Element;
    } | null>(null);

    useEffect(() => {
        const auth = getAuth();
        setUid(auth.currentUser?.uid ?? null);
    }, []);

    useEffect(() => {
        if (!uid) return;
        const q = query(collection(db, `users/${uid}/subscriptions`));
        const unsub = onSnapshot(q, snap => {
            setSubs(snap.docs.map(d => d.data() as SubscriptionDoc));
        });
        return () => unsub();
    }, [uid]);

    const editingSub = editing
        ? subs.find(s => s.event_id === editing.eventId) ?? null
        : null;
    const { update: updateEditing, unsubscribe: unsubscribeEditing } =
        useSubscription(editing?.eventId ?? '');

    return (
        <Card>
            <CardContent>
                <Typography variant="overline" sx={{ fontWeight: 600 }}>
                    Subscribed events ({subs.length})
                </Typography>
                {subs.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                    >
                        Visit any event and subscribe to start getting
                        notifications.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Subscribed</TableCell>
                                <TableCell>Types</TableCell>
                                <TableCell>Min severity</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subs.map(sub => (
                                <TableRow key={sub.event_id} hover>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                openInNewTab(
                                                    `/markets/event/${sub.event_id}`
                                                )
                                            }
                                        >
                                            {sub.event_title ?? sub.event_id}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        {formatRelative(sub.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        {sub.alert_types === null
                                            ? `All (${Object.keys(ALERT_TYPE_LABELS).length})`
                                            : `${sub.alert_types.length} of ${Object.keys(ALERT_TYPE_LABELS).length}`}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={
                                                SEVERITY_LABEL[sub.min_severity]
                                            }
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={e =>
                                                setEditing({
                                                    eventId: sub.event_id,
                                                    anchor: e.currentTarget,
                                                })
                                            }
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            {editing && editingSub && (
                <SubscribePopover
                    open={true}
                    anchorEl={editing.anchor}
                    onClose={() => setEditing(null)}
                    mode="edit"
                    initialAlertTypes={editingSub.alert_types}
                    initialMinSeverity={editingSub.min_severity}
                    onSave={async ({ alert_types, min_severity }) => {
                        await updateEditing({ alert_types, min_severity });
                    }}
                    onUnsubscribe={async () => {
                        await unsubscribeEditing();
                    }}
                />
            )}
        </Card>
    );
}

function SubscribedTradersTable() {
    const [uid, setUid] = useState<string | null>(null);
    const [subs, setSubs] = useState<TraderSubscriptionDoc[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();
        setUid(auth.currentUser?.uid ?? null);
    }, []);

    useEffect(() => {
        if (!uid) return;
        const q = query(collection(db, `users/${uid}/trader_subscriptions`));
        const unsub = onSnapshot(q, snap => {
            setSubs(snap.docs.map(d => d.data() as TraderSubscriptionDoc));
        });
        return () => unsub();
    }, [uid]);

    const setSeverity = async (
        address: string,
        min_severity: TraderSubscriptionDoc['min_severity']
    ) => {
        if (!uid) return;
        await updateDoc(
            doc(db, `users/${uid}/trader_subscriptions/${address}`),
            { min_severity }
        );
    };
    const setEnabled = async (address: string, enabled: boolean) => {
        if (!uid) return;
        await updateDoc(
            doc(db, `users/${uid}/trader_subscriptions/${address}`),
            { enabled }
        );
    };
    const remove = async (address: string) => {
        if (!uid) return;
        await deleteDoc(
            doc(db, `users/${uid}/trader_subscriptions/${address}`)
        );
    };

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="overline" sx={{ fontWeight: 600 }}>
                    Followed traders ({subs.length})
                </Typography>
                {subs.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                    >
                        Follow a trader from their profile to get notified when
                        they trade.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Trader</TableCell>
                                <TableCell>Notifications</TableCell>
                                <TableCell>Min severity</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subs.map(sub => (
                                <TableRow key={sub.trader_address} hover>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                navigate(
                                                    `/markets/trader/${sub.trader_address}`
                                                )
                                            }
                                        >
                                            {sub.trader_label ??
                                                sub.trader_address}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            size="small"
                                            checked={sub.enabled !== false}
                                            onChange={e =>
                                                setEnabled(
                                                    sub.trader_address,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {SEVERITY_ORDER.map(s => (
                                                <Button
                                                    key={s}
                                                    size="small"
                                                    variant={
                                                        sub.min_severity === s
                                                            ? 'contained'
                                                            : 'outlined'
                                                    }
                                                    onClick={() =>
                                                        setSeverity(
                                                            sub.trader_address,
                                                            s
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 0,
                                                        px: 1,
                                                        py: 0.25,
                                                        fontSize: '0.65rem',
                                                    }}
                                                >
                                                    {SEVERITY_LABEL[s]}
                                                </Button>
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                remove(sub.trader_address)
                                            }
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * #118: per-account mute for gate approval requests. Lists exactly the
 * accounts the user is assigned to (own gate_operators doc, isSelf-
 * readable) — hidden entirely for non-operators. Gate alerts bypass all
 * other filters by design (operational duty), so this explicit mute is
 * the only opt-out.
 */
function GateApprovalsCard() {
    const { settings } = useNotificationSettings();
    const notify = useNotify();
    const [assigned, setAssigned] = useState<string[]>([]);
    const uid = getAuth().currentUser?.uid ?? null;

    useEffect(() => {
        if (!uid) return;
        return onSnapshot(
            doc(db, `gate_operators/${uid}`),
            snap => {
                const ids = snap.data()?.account_ids;
                setAssigned(Array.isArray(ids) ? ids : []);
            },
            () => setAssigned([])
        );
    }, [uid]);

    if (!uid || assigned.length === 0) return null;
    const muted = Array.isArray(settings.muted_gate_accounts)
        ? settings.muted_gate_accounts
        : [];

    // setDoc+merge, NOT updateDoc: an operator who never touched any other
    // notification setting has no settings doc yet, and updateDoc fails
    // with not-found on a missing doc (same reason useNotificationSettings
    // .save uses setDoc+merge).
    const setMuted = (address: string, mute: boolean) =>
        setDoc(
            doc(db, `users/${uid}/notification_settings/main`),
            {
                muted_gate_accounts: mute
                    ? arrayUnion(address)
                    : arrayRemove(address),
            },
            { merge: true }
        ).catch(() =>
            notify('Could not update gate notification setting', {
                type: 'error',
            })
        );

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="overline" sx={{ fontWeight: 600 }}>
                    Gate approvals
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ mb: 1 }}
                >
                    Approval requests for accounts you operate. Switch off to
                    stop notifications for an account — you can still clear its
                    gates in the gates view.
                </Typography>
                <Table size="small">
                    <TableBody>
                        {assigned.map(address => (
                            <TableRow key={address}>
                                <TableCell
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    }}
                                >
                                    {address}
                                </TableCell>
                                <TableCell align="right">
                                    <Switch
                                        size="small"
                                        checked={!muted.includes(address)}
                                        onChange={e =>
                                            setMuted(address, !e.target.checked)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

export function SettingsNotifications() {
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Notifications
            </Typography>
            <GlobalDeliveryCard />
            <GateApprovalsCard />
            <SubscribedEventsTable />
            <SubscribedTradersTable />
        </Box>
    );
}
